/**
 * walletPage — Gestisce la pagina Salvadanaio ECM.
 *
 * - Attivazione wallet (POST /v1/users/me/wallet/activate)
 * - Riscatto bonus compleanno (POST /v1/users/me/wallet/birthday-bonus)
 * - Aggiornamento saldo e movimenti dopo ogni azione
 */
export function registerWalletPage(Alpine) {
    Alpine.data('walletPage', (initial = {}) => ({
        active: initial.active ?? false,
        balance: initial.balance ?? 0,
        transactions: initial.transactions ?? [],
        links: initial._links ?? {},
        activating: false,
        redeemingBonus: false,
        bonusRedeemed: false,

        get hasBirthdayBonus() {
            return !this.bonusRedeemed && !!this.links?.birthday_bonus;
        },

        get formattedBalance() {
            return new Intl.NumberFormat('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(this.balance);
        },

        formatAmount(amount) {
            const abs = Math.abs(amount);
            return new Intl.NumberFormat('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(abs);
        },

        formatDate(dateStr) {
            if (!dateStr) return '—';
            const d = new Date(dateStr + 'T00:00:00');
            return d.toLocaleDateString('it-IT', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            });
        },

        async activate() {
            if (this.activating) return;
            this.activating = true;
            try {
                const res = await fetch(IKOS.apiBase + '/v1/users/me/wallet/activate', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': IKOS.authToken,
                    },
                });
                if (!res.ok) throw new Error('Errore attivazione');
                const data = await res.json();
                this.active = true;
                this.balance = data.balance ?? 0;
                // Ricarica per ottenere links e transazioni aggiornate
                await this.reload();
                window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: { type: 'success', message: 'Salvadanaio attivato con successo!' },
                }));
            } catch (e) {
                window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: { type: 'error', message: 'Errore durante l\'attivazione. Riprova.' },
                }));
            } finally {
                this.activating = false;
            }
        },

        async redeemBirthday() {
            if (this.redeemingBonus) return;
            this.redeemingBonus = true;
            try {
                const res = await fetch(IKOS.apiBase + '/v1/users/me/wallet/birthday-bonus', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': IKOS.authToken,
                    },
                });
                if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    throw new Error(err.detail || 'Errore riscatto bonus');
                }
                const data = await res.json();
                this.balance = data.new_balance ?? this.balance;
                this.bonusRedeemed = true;
                await this.reload();
                const bonus = data.bonus_amount
                    ? new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2 }).format(data.bonus_amount)
                    : '';
                window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: {
                        type: 'success',
                        message: bonus
                            ? `Bonus compleanno di € ${bonus} riscattato!`
                            : 'Bonus compleanno riscattato!',
                    },
                }));
            } catch (e) {
                window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: { type: 'error', message: e.message || 'Errore durante il riscatto. Riprova.' },
                }));
            } finally {
                this.redeemingBonus = false;
            }
        },

        async reload() {
            try {
                const res = await fetch(IKOS.apiBase + '/v1/users/me/wallet', {
                    headers: { 'Auth-Token': IKOS.authToken },
                });
                if (!res.ok) return;
                const data = await res.json();
                this.active = data.active ?? this.active;
                this.balance = data.balance ?? this.balance;
                this.transactions = data.transactions ?? this.transactions;
                this.links = data._links ?? this.links;
            } catch (e) {
                // silently fail
            }
        },
    }));
}
